import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Trans, useTranslation } from 'react-i18next';
import { siteLanguage } from '@/lib/accountLanguage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { t } = useTranslation();

  // SEO: Prevent indexing of password reset page
  useEffect(() => {
    document.title = 'Forgot Password | Ridalo';
    const metaRobots = document.querySelector('meta[name="robots"]') || document.createElement('meta');
    metaRobots.setAttribute('name', 'robots');
    metaRobots.setAttribute('content', 'noindex, nofollow');
    if (!document.querySelector('meta[name="robots"]')) {
      document.head.appendChild(metaRobots);
    }
    return () => {
      if (metaRobots.parentNode) {
        metaRobots.remove();
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email, language: siteLanguage() });
      setSent(true);
    } catch (err) {
      toast.error(t('auth.forgot.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-ink-light hover:text-primary mb-6"
          data-testid="back-to-login-link"
        >
          <ArrowLeft className="w-4 h-4" /> {t('auth.forgot.backToLogin')}
        </Link>
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8">
          {sent ? (
            <div className="text-center" data-testid="forgot-password-sent">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-primary mb-3">{t('auth.forgot.sentTitle')}</h1>
              <p className="text-sm text-ink-light mb-2">
                <Trans
                  i18nKey="auth.forgot.sentBody"
                  values={{ email }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <p className="text-xs text-ink-muted mb-6">
                {t('auth.forgot.sentNote')}
              </p>
              <Link
                to="/login"
                className="text-accent hover:text-accent-hover font-medium text-sm"
              >
                {t('auth.forgot.backToLogin')}
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-primary mb-2">{t('auth.forgot.title')}</h1>
              <p className="text-sm text-ink-light mb-6">
                {t('auth.forgot.subtitle')}
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-primary mb-2 block">{t('auth.forgot.emailLabel')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('auth.forgot.emailPlaceholder')}
                      className="input-field pl-10"
                      required
                      autoFocus
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={loading}
                  data-testid="forgot-submit"
                >
                  {loading ? t('auth.forgot.sending') : t('auth.forgot.submit')}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
